import * as React from 'react';
import { connect } from 'react-redux';
import { actions } from 'react-redux-form';
import { Form, Icon, IconButton, Tooltip, Notification, Box } from '../';
import FIELDS from '../../data/fields';
import { updateBooking, payBooking } from '../../redux/actions/bookings';
import { getCompany } from '../../redux/actions/companies';
import Loader from '../Loader/Loader';

type IPaymentType = 'CASH' | 'CARD' | 'BACS' | 'CHEQUE' | 'CREDIT-NOTE';

interface Props {
  booking: any;
  isFullPayment: boolean;
  isAccountResolution: boolean;
  paymentButtonText: string;
  handleUpdate: Function;
  dispatch: Function;
}

interface State {
  errorMessage: string | boolean;
  isLoaded: boolean;
  hasAccount: boolean;
  type?: IPaymentType;
  total?: number;
  origin: string;
}

/**
 * Sub-component for a booking used for taking payment
 */
class BookingPayment extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      errorMessage: false,
      isLoaded: false,
      hasAccount: false,
      origin: 'SYSTEM',
    };
    this.payNormal = this.payNormal.bind(this);
    this.payOnAccount = this.payOnAccount.bind(this);
    this.payPreAuth = this.payPreAuth.bind(this);
  }

  async componentDidMount() {
    let hasAccount: boolean = false;

    if (this.props.booking.company_id) {
      const {
        error,
        data: { has_account, account_type, account_card_expiry },
      } = await this.props.dispatch(getCompany(this.props.booking.company_id));
      if (!error) {
        hasAccount =
          has_account &&
          account_type !== null &&
          (account_type === 'MONTHLY' || account_card_expiry !== null);
      }
    }
    this.setState({ hasAccount, isLoaded: true });
  }

  /**
   * Mark the booking as paid
   */
  async payNormal(total: number, type: IPaymentType) {
    const booking = this.props.booking;
    if (window.confirm(`Log this payment of £${Math.abs(total).toFixed(2)}?`)) {
      const payload: any = {
        type,
        total,
        origin: this.state.origin,
        is_account_payment: this.props.isAccountResolution === true,
      };
      if (booking.company_id) {
        payload.company_id = booking.company_id;
      }
      const response = await this.props.dispatch(
        payBooking(booking.id, payload),
      );
      this.props.handleUpdate(response);
    }
  }

  /**
   * Mark the booking to be paid on account
   */
  async payOnAccount() {
    const booking = this.props.booking;
    if (
      window.confirm('Mark payment for this booking as an account payment?')
    ) {
      const response = await this.props.dispatch(
        updateBooking(booking.id, {
          payment_status: 2,
        }),
      );
      this.props.handleUpdate(response);
      this.setState({ type: undefined, total: undefined });
    }
  }

  /**
   * Mark the booking as a pre-auth payment
   */
  async payPreAuth() {
    const booking = this.props.booking;
    const preAuthCost =
      Number(booking.hire_cost) + Number(booking.security_cost);
    if (
      window.confirm(
        'Pay a Pre-Auth payment of ' + `"£${preAuthCost.toFixed(2)}"?`,
      )
    ) {
      const payload: any = {
        type: 'PRE-AUTH',
        total: preAuthCost,
        origin: this.state.origin,
      };
      if (booking.company_id) {
        payload.company_id = booking.company_id;
      }
      const response = await this.props.dispatch(
        payBooking(booking.id, payload),
      );
      this.props.handleUpdate(response);
      this.setState({ type: undefined, total: undefined });
    }
  }

  render() {
    if (!this.state.isLoaded) {
      return <Loader fullwidth />;
    }
    const booking = this.props.booking;
    const totalOutstanding = Number(
      (Number(booking.total_cost) - Number(booking.total_paid)).toFixed(2),
    );
    const body = [];
    const selectNormalPaymentButton = (total: number, type: IPaymentType) => {
      if (!this.props.isFullPayment) {
        this.setState({
          total,
          type,
        });
      } else {
        this.payNormal(total, type);
      }
    };

    /**
     * The normal payment method buttons
     */
    const normalPaymentButtons = [
      <IconButton
        key="cash"
        icon="money"
        className={this.state.type === 'CASH' ? 'is-primary' : 'is-default'}
        handleClick={() => selectNormalPaymentButton(totalOutstanding, 'CASH')}
      >
        Cash
      </IconButton>,
      <IconButton
        key="card"
        icon="credit-card"
        className={this.state.type === 'CARD' ? 'is-primary' : 'is-default'}
        handleClick={() => selectNormalPaymentButton(totalOutstanding, 'CARD')}
      >
        Card
      </IconButton>,
      <IconButton
        key="bacs"
        icon="credit-card"
        className={this.state.type === 'BACS' ? 'is-primary' : 'is-default'}
        handleClick={() => selectNormalPaymentButton(totalOutstanding, 'BACS')}
      >
        BACS
      </IconButton>,
      <IconButton
        key="cheque"
        icon="book"
        className={this.state.type === 'CHEQUE' ? 'is-primary' : 'is-default'}
        handleClick={() =>
          selectNormalPaymentButton(totalOutstanding, 'CHEQUE')
        }
      >
        Cheque
      </IconButton>,
    ];

    switch (booking.payment_status) {
      // Standard payment methods
      case 0:
      case 1:
      case 4:
        body.push(...normalPaymentButtons);
        // Only show "to pay on account" option if no previous payment has been taken
        if (booking.total_paid === 0 && this.state.hasAccount) {
          body.push(
            <IconButton
              key="to-pay-on-account"
              icon="university"
              className="is-default"
              handleClick={() => this.payOnAccount()}
            >
              Will Pay on Account
            </IconButton>,
          );
        }

        // Only allow Pre-Auth if unpaid and not returned
        if (
          booking.payment_status === 0 &&
          booking.status !== 'ON-HOLD' &&
          booking.total_paid === 0
        ) {
          body.push(
            <IconButton
              key="pre-auth"
              icon="credit-card"
              className="is-default"
              handleClick={() => this.payPreAuth()}
            >
              Pre-Auth
            </IconButton>,
          );
        }
        break;

      // Reverting from "to pay on account" to "unpaid"
      case 2:
        if (this.props.isAccountResolution) {
          body.push(...normalPaymentButtons);
        } else if (
          ['PENDING', 'ON-HOLD'].indexOf(this.props.booking.status) !== -1
        ) {
          body.push(
            <IconButton
              key="to-pay-on-account"
              icon="undo"
              className="is-default field"
              handleClick={async () => {
                if (
                  window.confirm(
                    'Really revert this back to an unpaid booking?',
                  )
                ) {
                  const response = await this.props.dispatch(
                    updateBooking(booking.id, {
                      payment_status: 0,
                    }),
                  );
                  this.props.handleUpdate(response);
                }
              }}
            >
              Revert Pending Account Payment
            </IconButton>,
          );
        }
    }

    // Always give the option to create a credit note
    body.push(
      <IconButton
        key="credit-note"
        icon="gift"
        className={
          this.state.type === 'CREDIT-NOTE' ? 'is-primary' : 'is-default'
        }
        handleClick={() =>
          selectNormalPaymentButton(totalOutstanding, 'CREDIT-NOTE')
        }
      >
        Credit Note
      </IconButton>,
    );

    return (
      <div>
        {totalOutstanding < 0 && (
          <Notification type="danger">
            <div>
              <Icon icon="exclamation-triangle" />{' '}
              <strong>Overpayment Detected</strong>
              <p>
                According to our records, this booking has been overpaid by{' '}
                <strong>£{Math.abs(totalOutstanding)}</strong>.
              </p>
              <IconButton
                icon="money"
                className="is-danger is-inverted is-outlined"
                handleClick={async () => {
                  const response = await this.props.dispatch(
                    payBooking(booking.id, {
                      total: Number(totalOutstanding),
                      type: 'CREDIT-NOTE',
                    }),
                  );
                  this.props.handleUpdate(response);
                }}
              >
                Resolve via Credit Note
              </IconButton>
            </div>
          </Notification>
        )}
        <Box key="booking-payment">
          <h4 className="heading">Log a Payment</h4>
          <div className="field">
            <label htmlFor="origin">Origin</label>
            <div className="select">
              <select
                id="origin"
                name="origin"
                onChange={({ target: { value } }) =>
                  this.setState({ origin: value })
                }
                value={this.state.origin}
              >
                <option value="SYSTEM">Booking System</option>
                <option value="SAGEPAY">Online Payment (Sage Pay)</option>
              </select>
            </div>
          </div>
          <label>Type</label>
          <div className="buttons has-addons">
            {body.map((button) => button)}
          </div>
          {this.state.total !== undefined && this.state.type !== undefined && (
            <Form
              fields={FIELDS.payments.normal}
              type="partial"
              handleSubmit={(data) => {
                if (
                  Number(data.total) >= 0 &&
                  this.state.type === 'CREDIT-NOTE'
                ) {
                  window.alert('Credit notes must be of negative value');
                  return;
                }

                if (
                  Number(data.total) < 0 &&
                  this.state.type !== 'CREDIT-NOTE'
                ) {
                  window.alert('Payments must be of positive value');
                  return;
                }

                this.payNormal(Number(data.total), this.state.type);
                this.setState({ type: undefined, total: undefined });
              }}
              getDispatch={(dispatch) => {
                dispatch(
                  actions.change(
                    'partial.total',
                    Number(this.state.total).toFixed(2),
                  ),
                );
              }}
              buttonText={
                this.props.paymentButtonText
                  ? this.props.paymentButtonText
                  : `Log Payment`
              }
            />
          )}
        </Box>
      </div>
    );
  }
}

export default connect()(BookingPayment);
